import { useEffect, useMemo, useState } from "react";
import { X, ShieldAlert, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useHistoryModal from "@/hooks/useHistoryModal";

// UGC categories (mirrors backend REPORT_CATEGORIES enum).
const CATEGORIES = [
  { key: "spam",             label: "Spam or misleading" },
  { key: "harassment",       label: "Harassment or bullying" },
  { key: "hate_or_violence", label: "Hate speech or violence" },
  { key: "sexual_content",   label: "Sexual or explicit content" },
  { key: "misinformation",   label: "False information" },
  { key: "counterfeit",      label: "Counterfeit or IP infringement" },
  { key: "privacy_violation",label: "Privacy violation" },
  { key: "child_safety",     label: "Child safety concern" },
  { key: "self_harm",        label: "Self-harm or suicide content" },
  { key: "illegal",          label: "Illegal activity" },
  { key: "other",            label: "Something else" },
];

const SUBJECT_LABELS = {
  review:  "Review",
  product: "Product",
  user:    "Member",
  general: "The app",
};

// hCaptcha public site key — read from env at build time. When missing (dev)
// we render a plain "I am not a robot" checkbox that submits the "dev-bypass"
// token; the backend accepts it only when its HCAPTCHA_SECRET is unset.
const HCAPTCHA_SITEKEY = process.env.REACT_APP_HCAPTCHA_SITEKEY || "";

const ReportModal = ({ open, onClose, subjectType = "general", subjectId = null, subjectLabel }) => {
  const { user } = useAuth();
  useHistoryModal(open, onClose);

  const [category, setCategory] = useState("spam");
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // {duplicate?: bool}

  // Reset every time the modal opens.
  useEffect(() => {
    if (!open) return;
    setCategory("spam");
    setReason("");
    setDescription("");
    setContactEmail(user?.email || "");
    setCaptchaToken("");
    setSubmitting(false);
    setDone(null);
  }, [open, user?.email]);

  // Load hCaptcha script only when needed AND only for guests.
  const needsCaptcha = !user;
  useEffect(() => {
    if (!open || !needsCaptcha || !HCAPTCHA_SITEKEY) return;
    if (document.getElementById("rl-hcaptcha-script")) return;
    const s = document.createElement("script");
    s.id = "rl-hcaptcha-script";
    s.src = "https://js.hcaptcha.com/1/api.js";
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
    // Global callback used by hCaptcha's `data-callback` attribute.
    window.__rlHCaptchaSuccess = (token) => setCaptchaToken(token || "");
  }, [open, needsCaptcha]);

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (reason.trim().length < 3) return false;
    if (!CATEGORIES.find((c) => c.key === category)) return false;
    if (needsCaptcha) {
      if (!contactEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) return false;
      if (!captchaToken) return false;
    }
    return true;
  }, [reason, category, needsCaptcha, contactEmail, captchaToken, submitting]);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        subject_type: subjectType,
        subject_id: subjectId || undefined,
        category,
        reason: reason.trim(),
        description: description.trim(),
      };
      if (needsCaptcha) {
        payload.contact_email = contactEmail.trim();
        payload.hcaptcha_token = captchaToken;
      }
      const { data } = await api.post("/reports", payload);
      setDone({ duplicate: !!data?.duplicate });
      if (data?.duplicate) {
        toast.message("Already reported — thank you.");
      } else {
        toast.success("Report received. Our team reviews within 48 hours.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center"
      data-testid="report-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-modal-title"
        className="relative flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden bg-white shadow-2xl sm:rounded-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center bg-[#FBF6E4] text-[#B8912B]">
              <ShieldAlert className="h-4 w-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#B8912B]">Report a problem</div>
              <h2 id="report-modal-title" className="font-heading text-lg">
                Flag {SUBJECT_LABELS[subjectType] || "content"}
                {subjectLabel ? <span className="ml-1 text-sm text-gray-500">· {subjectLabel}</span> : null}
              </h2>
            </div>
          </div>
          <button
            type="button"
            data-testid="report-modal-close"
            onClick={onClose}
            aria-label="Close report dialog"
            className="grid h-9 w-9 place-items-center text-gray-500 hover:text-black"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {done ? (
            <div className="py-6 text-center" data-testid="report-modal-success">
              <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-[#FBF6E4]">
                <CheckCircle2 className="h-7 w-7 text-[#B8912B]" />
              </div>
              <div className="font-heading text-xl">
                {done.duplicate ? "Already on file" : "Thank you"}
              </div>
              <p className="mx-auto mt-2 max-w-sm text-sm text-gray-600">
                {done.duplicate
                  ? "You've already reported this recently. Our team is on it."
                  : "Our moderators triage every report within 48 hours. You'll receive an update by email if action is taken."}
              </p>
              <button
                data-testid="report-modal-done-close"
                onClick={onClose}
                className="mt-6 bg-black px-6 py-3 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#B8912B]"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Category grid */}
              <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-gray-700">
                What's the problem?
              </label>
              <div
                data-testid="report-category-grid"
                className="grid grid-cols-2 gap-2"
                role="radiogroup"
                aria-label="Report category"
              >
                {CATEGORIES.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    role="radio"
                    aria-checked={category === c.key}
                    data-testid={`report-cat-${c.key}`}
                    onClick={() => setCategory(c.key)}
                    className={`border px-3 py-2.5 text-left text-[12px] transition-colors ${
                      category === c.key
                        ? "border-black bg-black text-white"
                        : "border-gray-200 bg-white text-gray-700 hover:border-black"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              {/* Short headline */}
              <div className="mt-5">
                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-gray-700">
                  Short summary
                </label>
                <input
                  data-testid="report-reason-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value.slice(0, 200))}
                  placeholder="e.g. Review contains profanity"
                  maxLength={200}
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-black"
                />
                <div className="mt-1 text-right text-[10px] text-gray-400">{reason.length}/200</div>
              </div>

              {/* Description */}
              <div className="mt-3">
                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-gray-700">
                  Additional details (optional)
                </label>
                <textarea
                  data-testid="report-description-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 4000))}
                  rows={4}
                  placeholder="Please share any context that will help our team act on this quickly."
                  className="w-full resize-none border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-black"
                />
              </div>

              {/* Guest fallback: contact + captcha */}
              {needsCaptcha && (
                <div
                  data-testid="report-guest-block"
                  className="mt-4 rounded-none border border-[#F2E8CD] bg-[#FBF6E4]/60 p-4"
                >
                  <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#B8912B]">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Contact & verification
                  </div>
                  <p className="mb-3 text-[12px] leading-relaxed text-gray-700">
                    We'll email you if we need more information. Signed-in members can skip this step.
                  </p>
                  <input
                    data-testid="report-email-input"
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value.trim().slice(0, 200))}
                    placeholder="you@example.com"
                    className="w-full border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-black"
                  />

                  <div className="mt-3">
                    {HCAPTCHA_SITEKEY ? (
                      <div
                        className="h-captcha"
                        data-testid="report-hcaptcha"
                        data-sitekey={HCAPTCHA_SITEKEY}
                        data-callback="__rlHCaptchaSuccess"
                        data-theme="light"
                      />
                    ) : (
                      <label
                        data-testid="report-hcaptcha-devbypass"
                        className={`flex cursor-pointer items-center gap-2 border px-3 py-3 text-[13px] ${
                          captchaToken ? "border-black bg-[#FBF6E4]" : "border-gray-200"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={!!captchaToken}
                          onChange={(e) => setCaptchaToken(e.target.checked ? "dev-bypass" : "")}
                          className="h-4 w-4 accent-black"
                        />
                        <span>
                          I'm a real person <span className="text-gray-400">(dev captcha)</span>
                        </span>
                      </label>
                    )}
                  </div>
                </div>
              )}

              {/* SLA / policy note */}
              <p className="mt-4 text-[11px] leading-relaxed text-gray-500">
                By submitting a report you agree to our <a href="/terms" className="underline">Terms</a> and
                <span> </span>
                <a href="/privacy" className="underline">Privacy Policy</a>.
                Malicious or repeat false reports may result in account action.
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        {!done && (
          <div className="border-t border-gray-100 px-5 py-4">
            <div className="flex gap-2">
              <button
                data-testid="report-modal-cancel"
                type="button"
                onClick={onClose}
                className="flex-1 border border-gray-200 py-3 text-[11px] font-semibold uppercase tracking-widest hover:border-black"
              >
                Cancel
              </button>
              <button
                data-testid="report-modal-submit"
                type="button"
                disabled={!canSubmit}
                onClick={submit}
                className="flex flex-[2] items-center justify-center gap-2 bg-black py-3 text-[11px] font-semibold uppercase tracking-widest text-white transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Send className="h-3.5 w-3.5" />
                {submitting ? "Sending…" : "Submit report"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportModal;
